import { ActionIcon, Avatar, Menu, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDots, IconTrash, IconUserMinus, IconUserPlus } from '@tabler/icons-react';
import { api } from '@/clients/admin.client';
import { UserInfo } from '@/lib/auth';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { useRouter } from '@tanstack/react-router';
import { User } from '@/clients/types';

type Props = {
  adminClient: typeof api;
  users: User[];
  userInfo: UserInfo;
};

export function ActiveUserList({ users, userInfo, adminClient }: Props) {
  const router = useRouter();
  const handleRemoveUser = async (user: User) => {
    try {
      await adminClient.admin_v1_remove_user(undefined, {
        params: { user_id: user.user_id },
      });
      await router.load();
      notifySuccess(`ユーザ ${user.email} を組織メンバーから外しました。`);
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} を組織メンバーから外すことに失敗しました。`);
    }
  };

  const handleAssignAdminRole = async (user: User) => {
    try {
      await adminClient.admin_v1_update_user_role(
        {
          role: 'admin',
        },
        {
          params: { user_id: user.user_id },
        }
      );
      await router.load();
      notifySuccess(`ユーザ ${user.email} をテナント管理者に変更しました。`);
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} の権限変更に失敗しました。`);
    }
  };

  const handleRevokeAdminRole = async (user: User) => {
    try {
      await adminClient.admin_v1_update_user_role(
        {
          role: 'user',
        },
        {
          params: { user_id: user.user_id },
        }
      );
      await router.load();
      notifySuccess(`ユーザ ${user.email} を一般ユーザに変更しました。`);
    } catch (error) {
      logger.error(error);
      notifyError(`ユーザ ${user.email} の権限変更に失敗しました。`);
    }
  };

  const openConfirmRemoveUserModal = (user: User) => {
    modals.openConfirmModal({
      title: 'ユーザを組織メンバーから外しますか？',
      children: (
        <Text size="sm">
          この操作により、 <strong>{user.email}</strong> は組織メンバーから外されます。
          元に戻すには、再度招待する必要があります。
        </Text>
      ),
      onConfirm: () => handleRemoveUser(user),
    });
  };

  return (
    <Table.ScrollContainer minWidth={700} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th>メールアドレス</Table.Th>
            <Table.Th>名前</Table.Th>
            <Table.Th>権限</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.map((user, i) => (
            <Table.Tr key={i}>
              <Table.Td data-stack-hidden>
                {(user.picture && <Avatar src={user.picture} alt={user.name} />) || (
                  <Avatar radius="xl" />
                )}
              </Table.Td>
              <Table.Td data-label="メールアドレス">{user.email}</Table.Td>
              <Table.Td data-label="名前">{user.name}</Table.Td>
              <Table.Td data-label="権限">
                {user.role === 'admin' ? 'テナント管理者' : '一般ユーザ'}
              </Table.Td>
              <Table.Td data-action>
                <Menu>
                  <Menu.Target>
                    <ActionIcon variant="outline" size="md">
                      <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {userInfo.userId === user.user_id && (
                      <Menu.Label>ログイン中のユーザは変更できません。</Menu.Label>
                    )}
                    {user.role === 'admin' ? (
                      <Menu.Item
                        leftSection={<IconUserMinus style={{ width: '70%', height: '70%' }} />}
                        onClick={() => {
                          handleRevokeAdminRole(user);
                        }}
                        disabled={userInfo.userId === user.user_id}
                      >
                        一般ユーザに変更
                        <Text size="xs" c="gray">
                          プロジェクトメンバーに追加することで
                          <br />
                          そのプロジェクトの操作ができます。
                        </Text>
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        leftSection={<IconUserPlus style={{ width: '70%', height: '70%' }} />}
                        onClick={() => {
                          handleAssignAdminRole(user);
                        }}
                        disabled={userInfo.userId === user.user_id}
                      >
                        テナント管理者に変更
                        <Text size="xs" c="gray">
                          すべてのプロジェクトの管理者権限を持ち、
                          <br />
                          請求・ユーザ管理などの設定ができます。
                        </Text>
                      </Menu.Item>
                    )}

                    <Menu.Item
                      leftSection={
                        <IconTrash
                          style={{ width: '70%', height: '70%' }}
                          className="text-plamo-error"
                        />
                      }
                      onClick={() => {
                        openConfirmRemoveUserModal(user);
                      }}
                      disabled={userInfo.userId === user.user_id}
                    >
                      <div className="text-plamo-error">組織メンバーから外す</div>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
